/**
 * Shared helpers for the vokabeln card front and back.
 *
 * Lives outside the component files so both can import it without breaking
 * fast refresh (which requires a component module to export only components).
 */
import type { Card, VokabelnPayload } from '@/core/types';

export type VokabelCard = Card<VokabelnPayload>;

/**
 * The correct English gloss.
 *
 * Stored in `cards.translations.en[0]` rather than in the payload, so the
 * column keeps the same meaning it has in every other app — "what this card
 * means in the user's language".
 */
export function correctGloss(card: VokabelCard): string {
  return card.translations.en?.[0] ?? '';
}

/**
 * Whether a chosen option is the right answer.
 *
 * `null` means the user pressed "I don't know" and is never correct — the
 * first release of this app shipped without that button, and its replacement
 * ("I already know this") sat in the same screen position while meaning the
 * opposite, so a miss was being recorded as a correct answer. Kept as a named
 * function so that case stays covered by a test.
 */
export function isAnswerCorrect(chosen: string | null, answer: string): boolean {
  return chosen !== null && chosen === answer;
}

/**
 * Gender tint for the card's left border and for the article word, reusing the
 * tokens the two noun apps already use (der = blue, die = red, das = green).
 * Non-nouns get the neutral border.
 *
 * Re-exported under the names this app's components already import; the
 * definition lives in `@/lib/gender` now that the end-of-session miss list
 * needs the same tint.
 */
export { articleBorder as genderBorder, articleText as genderText } from '@/lib/gender';
